import React, { useState, useEffect } from 'react';
import { Clock, Save, Loader2, Check, X, AlertCircle } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

const AvailabilitySettings = ({ psychologistId, onSave }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');

    const [availability, setAvailability] = useState({
        slots: [
            { dayOfWeek: 1, startTime: '09:00', endTime: '18:00', active: true },
            { dayOfWeek: 2, startTime: '09:00', endTime: '18:00', active: true },
            { dayOfWeek: 3, startTime: '09:00', endTime: '18:00', active: true },
            { dayOfWeek: 4, startTime: '09:00', endTime: '18:00', active: true },
            { dayOfWeek: 5, startTime: '09:00', endTime: '18:00', active: true },
            { dayOfWeek: 6, startTime: '10:00', endTime: '14:00', active: false },
            { dayOfWeek: 7, startTime: '10:00', endTime: '14:00', active: false },
        ],
        slotDuration: 60,
        timezone: 'Europe/Bucharest'
    });

    const dayNames = ['', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'];

    // Încarcă disponibilitatea existentă
    useEffect(() => {
        const loadAvailability = async () => {
            if (!psychologistId) return;

            try {
                const availDoc = await getDoc(doc(db, 'availability', psychologistId));
                if (availDoc.exists()) {
                    const data = availDoc.data();
                    // Asigură-te că avem toate zilele
                    const existingSlots = data.slots || [];
                    const fullSlots = [];

                    for (let day = 1; day <= 7; day++) {
                        const existing = existingSlots.find(s => s.dayOfWeek === day);
                        if (existing) {
                            fullSlots.push(existing);
                        } else {
                            fullSlots.push({
                                dayOfWeek: day,
                                startTime: '09:00',
                                endTime: '18:00',
                                active: false
                            });
                        }
                    }

                    setAvailability({
                        slots: fullSlots,
                        slotDuration: data.slotDuration || 60,
                        timezone: data.timezone || 'Europe/Bucharest'
                    });
                }
            } catch (err) {
                console.error('Eroare la încărcare disponibilitate:', err);
                setError('Nu am putut încărca programul.');
            }
            setIsLoading(false);
        };

        loadAvailability();
    }, [psychologistId]);

    // Toggle zi activă/inactivă
    const toggleDay = (dayOfWeek) => {
        setAvailability(prev => ({
            ...prev,
            slots: prev.slots.map(slot =>
                slot.dayOfWeek === dayOfWeek
                    ? { ...slot, active: !slot.active }
                    : slot
            )
        }));
        setSaved(false);
    };

    // Actualizează ora
    const updateTime = (dayOfWeek, field, value) => {
        setAvailability(prev => ({
            ...prev,
            slots: prev.slots.map(slot =>
                slot.dayOfWeek === dayOfWeek
                    ? { ...slot, [field]: value }
                    : slot
            )
        }));
        setSaved(false);
    };

    // Actualizează durata slotului
    const updateSlotDuration = (duration) => {
        setAvailability(prev => ({
            ...prev,
            slotDuration: parseInt(duration)
        }));
        setSaved(false);
    };

    // Salvează
    const handleSave = async () => {
        setIsSaving(true);
        setError('');

        try {
            await setDoc(doc(db, 'availability', psychologistId), {
                ...availability,
                updatedAt: new Date()
            });
            setSaved(true);
            if (onSave) onSave();

            // Reset saved message după 3 secunde
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            console.error('Eroare la salvare:', err);
            setError('Nu am putut salva programul.');
        }

        setIsSaving(false);
    };

    // Generează opțiuni pentru ore
    const timeOptions = [];
    for (let h = 6; h <= 22; h++) {
        for (let m = 0; m < 60; m += 30) {
            const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
            timeOptions.push(time);
        }
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin text-purple-600" size={32} />
                <span className="ml-3 text-slate-600">Se încarcă programul...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-xl font-bold text-slate-900">Setări program</h3>
                    <p className="text-slate-600">Configurează zilele și orele în care ești disponibil(ă).</p>
                </div>

                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${saved
                            ? 'bg-green-600 text-white'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        } disabled:opacity-50`}
                >
                    {isSaving ? (
                        <>
                            <Loader2 className="animate-spin" size={20} />
                            Se salvează...
                        </>
                    ) : saved ? (
                        <>
                            <Check size={20} />
                            Salvat!
                        </>
                    ) : (
                        <>
                            <Save size={20} />
                            Salvează
                        </>
                    )}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-center gap-2">
                    <AlertCircle size={20} />
                    {error}
                </div>
            )}

            {/* Durată slot */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                    <Clock size={16} className="inline mr-2" />
                    Durată interval (include pauza între ședințe)
                </label>
                <select
                    value={availability.slotDuration}
                    onChange={(e) => updateSlotDuration(e.target.value)}
                    className="w-full md:w-auto px-4 py-2 border-2 border-slate-200 rounded-lg focus:border-purple-500 focus:outline-none"
                >
                    <option value={50}>50 minute (fără pauză)</option>
                    <option value={60}>60 minute (50 min + 10 min pauză)</option>
                    <option value={75}>75 minute (60 min + 15 min pauză)</option>
                    <option value={90}>90 minute (pentru sesiuni mai lungi)</option>
                </select>
                <p className="text-xs text-slate-500 mt-2">
                    Acesta e intervalul dintre începuturile a două ședințe consecutive.
                </p>
            </div>

            {/* Zile și ore */}
            <div className="space-y-3">
                {availability.slots.map(slot => (
                    <div
                        key={slot.dayOfWeek}
                        className={`flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-xl border-2 transition-all ${slot.active
                                ? 'bg-white border-purple-200 shadow-sm'
                                : 'bg-slate-50 border-slate-200'
                            }`}
                    >
                        {/* Toggle + Nume zi */}
                        <div className="flex items-center gap-3 md:w-40">
                            <button
                                onClick={() => toggleDay(slot.dayOfWeek)}
                                className={`w-12 h-7 rounded-full relative transition-colors ${slot.active ? 'bg-purple-600' : 'bg-slate-300'
                                    }`}
                            >
                                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${slot.active ? 'left-6' : 'left-1'
                                    }`} />
                            </button>
                            <span className={`font-semibold ${slot.active ? 'text-slate-900' : 'text-slate-400'}`}>
                                {dayNames[slot.dayOfWeek]}
                            </span>
                        </div>

                        {/* Ore */}
                        {slot.active ? (
                            <div className="flex items-center gap-3 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-slate-600">De la</span>
                                    <select
                                        value={slot.startTime}
                                        onChange={(e) => updateTime(slot.dayOfWeek, 'startTime', e.target.value)}
                                        className="px-3 py-2 border-2 border-slate-200 rounded-lg focus:border-purple-500 focus:outline-none bg-white"
                                    >
                                        {timeOptions.map(time => (
                                            <option key={time} value={time}>{time}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-slate-600">până la</span>
                                    <select
                                        value={slot.endTime}
                                        onChange={(e) => updateTime(slot.dayOfWeek, 'endTime', e.target.value)}
                                        className="px-3 py-2 border-2 border-slate-200 rounded-lg focus:border-purple-500 focus:outline-none bg-white"
                                    >
                                        {timeOptions.map(time => (
                                            <option key={time} value={time}>{time}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 text-slate-400 italic">
                                Indisponibil
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-800 text-sm">
                    <strong>💡 Sfat:</strong> Clienții vor putea programa doar în intervalele active.
                    Dacă ai nevoie să blochezi o zi specifică (vacanță, conferință),
                    folosește funcția "Blochează zi" din calendar.
                </p>
            </div>
        </div>
    );
};

export default AvailabilitySettings;